import { insertAndSyncOrderEntryItems } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/insert-and-sync-order-entry-items';
import {
  orderEntryCollection,
  orderGuideItemCollection,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { SalesRepConfig } from '@domain/configuration/types';
import { transformOrderEntryItem } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { useCallback, useRef } from 'react';
import { useCartStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useIsFocused } from '@react-navigation/native';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { useSavedOrderEntryItemTransformer } from '@/experiences/sales-portal/domain/ordering/blocs/transformers/use-cart-transformers';
import type {
  LoadSalesPortalCartQuery,
  OrderEntryDataQuery,
  OrderEntryPriceEditDisplayHeader,
  SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment,
} from '@/types';
import type { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';

/**
 * Params for syncing order entry server data. Passed to {@link UseOrderEntrySyncResult.synchronizeCartStore}.
 */
export interface OrderEntrySyncParams {
  /** Saved fulfillment selection for the restaurant. */
  fulfillment: SavedSelectedFulfillment | null;
  /** Restaurant UUID. */
  restaurantUUID: string;
  /** Supplier timezone (IANA). */
  supplierTimezone: string;
  /** Supplier UUID. */
  supplierUUID: string;
}

/** Cart data shape for {@link UseOrderEntrySyncResult.synchronizeCartStore}. */
export interface OrderEntrySyncCartData {
  getOrderEntryItemsFromCart: LoadSalesPortalCartQuery['getOrderEntryItemsFromCart'];
}

/** Order guide data shape for {@link UseOrderEntrySyncResult.synchronizeOrderEntryCollections}. */
export interface OrderEntrySyncOrderEntryData {
  getOrderEntryItemsFromOrderGuide: OrderEntryDataQuery['orderGuide'];
}

export interface UseOrderEntrySyncResult {
  /**
   * Writes cart into cart store and merges cart-only items into collections.
   * Call after {@link synchronizeOrderEntryCollections}. Cart-only rows are
   * stamped with the same guide-level `priceEditDisplayHeaders` as guide rows
   * so the item-detail panel reads consistent headers regardless of which row
   * is selected.
   */
  synchronizeCartStore: (
    params: OrderEntrySyncParams,
    cartData: OrderEntrySyncCartData | null | undefined,
    priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[],
  ) => void;
  /** Writes order guide table data into order-entry collections. Call first. */
  synchronizeOrderEntryCollections: (
    orderEntryData: OrderEntrySyncOrderEntryData | null | undefined,
  ) => void;
  /** Updates/creates PriceEditor instances for each order entry item. Call after collections and cart are synced. */
  synchronizePriceEditors: (
    items: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment[],
    salesRepConfig: SalesRepConfig,
  ) => void;
}

/**
 * Hook that returns two sync functions. Call them in order when server data is ready (e.g. after
 * both queries have resolved in the initial data loader): first
 * {@link UseOrderEntrySyncResult.synchronizeOrderEntryCollections}, then
 * {@link UseOrderEntrySyncResult.synchronizeCartStore}.
 */
export const useOrderEntrySync = (): UseOrderEntrySyncResult => {
  // whether the order-entry screen this hook belongs to is the focused one.
  const isFocused = useIsFocused();
  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;

  const synchronizeOrderEntryCollections = useCallback(
    (orderEntryData: OrderEntrySyncOrderEntryData | null | undefined): void => {
      const orderGuide = orderEntryData?.getOrderEntryItemsFromOrderGuide;
      const orderEntryItems = orderGuide?.order_entry_items;
      const priceEditDisplayHeaders =
        (orderGuide?.price_edit_display_headers as
          | OrderEntryPriceEditDisplayHeader[]
          | undefined) ?? [];
      const orderEntryKeys = new Set(
        orderEntryItems?.map((item) => item.variant_pack_id) ?? [],
      );

      if (orderEntryKeys.size > 0) {
        const guideKeysToDelete = [...orderGuideItemCollection.keys()].filter(
          (key) => orderEntryKeys.has(key as string),
        );
        const entryKeysToDelete = [...orderEntryCollection.keys()].filter(
          (key) => orderEntryKeys.has(key as string),
        );
        if (guideKeysToDelete.length > 0) {
          orderGuideItemCollection.delete(guideKeysToDelete);
        }
        if (entryKeysToDelete.length > 0) {
          orderEntryCollection.delete(entryKeysToDelete);
        }
        if (orderEntryItems && orderEntryItems.length > 0) {
          orderGuideItemCollection.insert(orderEntryItems);
          orderEntryCollection.insert(
            orderEntryItems.map((item, index) =>
              transformOrderEntryItem(item, index, priceEditDisplayHeaders),
            ),
          );
        }
      }
    },
    [],
  );

  const synchronizeCartStore = useCallback(
    (
      params: OrderEntrySyncParams,
      cartData: OrderEntrySyncCartData | null | undefined,
      priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[],
    ): void => {
      const { restaurantUUID, supplierUUID, supplierTimezone, fulfillment } =
        params;
      const rawCartItems =
        cartData?.getOrderEntryItemsFromCart?.order_entry_items;

      if (!rawCartItems || !fulfillment) {
        return;
      }

      const cartItems = rawCartItems.map(useSavedOrderEntryItemTransformer);
      const lastUpdatedAt = new Date().toISOString();
      const lastSavedAt = lastUpdatedAt;
      const orderUUID = rawCartItems[0]?.order_info?.order_id ?? null;

      // prevent syncing another restaurant's cart if the page has changed
      if (isFocusedRef.current) {
        useCartStore.getState().setCart({
          cartItems,
          fulfillment,
          lastSavedAt,
          lastUpdatedAt,
          orderUUID,
          restaurantUUID,
          supplierTimezone,
          supplierUUID,
        });
      }

      insertAndSyncOrderEntryItems(rawCartItems, priceEditDisplayHeaders, null);
    },
    [],
  );

  const synchronizePriceEditors = useCallback(
    (
      items: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment[],
      salesRepConfig: SalesRepConfig,
    ): void => {
      if (items.length > 0) {
        usePricingStore.getState().syncEditors(items, salesRepConfig);
      }
    },
    [],
  );

  return {
    synchronizeCartStore,
    synchronizeOrderEntryCollections,
    synchronizePriceEditors,
  };
};
